// SPDX-License-Identifier: Elastic-2.0
/**
 * Token storage adapter (T-007).
 *
 * Centralises the localStorage keys used by the auth subsystem so the
 * Zustand `useAuth` store, the gateway/accounts API clients and the
 * router-level `AuthGuard` all agree on the same slots.
 *
 * Per F-310 technical spec § 6.5 and ADR-006:
 *   - `mirlai_jwt`        — cloud JWT issued by F-200 (Path A from A5).
 *   - `mirlai_refresh`    — opaque refresh token paired with the cloud JWT.
 *   - `mirlai_device_id`  — anonymous account-less `device_id` (UUID), the
 *                           identity for the Free tier (F-348 / ADR-002
 *                           Amendment §A.1). Replaces the retired
 *                           `mirlai_jwt_local` slot — the Free tier no
 *                           longer holds a locally-issued JWT.
 *   - `mirlai_instance`   — pairing record (F-202 handoff) — opaque blob
 *                           managed by onboarding code; we expose key
 *                           access here so `clearAll()` can wipe it on
 *                           sign-out without callers re-typing the literal.
 *
 * Every wrapper is wrapped in try/catch so a sandboxed runtime
 * (private mode quota exceeded, SSR, hardened tests) cannot crash
 * the boot path — failures degrade silently to "no token", which
 * matches the bearer-resolver contract in `gateway-client.ts` /
 * `accounts-client.ts` (empty string → skip `Authorization` header).
 */

/**
 * Canonical localStorage keys. Exported as a const map so tests + other
 * modules can reference the symbolic name (e.g. `KEYS.jwt`) instead of
 * re-typing the literal. AuthGuard + the two API clients read the
 * store (`useAuth`) which writes through to these exact keys.
 */
export const TOKEN_STORAGE_KEYS = {
  jwt: "mirlai_jwt",
  refresh: "mirlai_refresh",
  deviceId: "mirlai_device_id",
  instance: "mirlai_instance",
} as const;

export type TokenStorageKey = keyof typeof TOKEN_STORAGE_KEYS;

/**
 * Defensive check — happy-dom v20 + Node 25 sometimes leave
 * `globalThis.localStorage` undefined; SSR (future) has no `localStorage`
 * at all. Centralising the guard keeps every reader / writer one-liner.
 */
function safeStorage(): Storage | null {
  try {
    if (typeof localStorage === "undefined") {
      return null;
    }
    return localStorage;
  } catch {
    return null;
  }
}

export const tokenStorage = {
  /** Read a slot. Returns `null` if missing or storage is unavailable. */
  read(key: TokenStorageKey): string | null {
    const storage = safeStorage();
    if (!storage) return null;
    try {
      return storage.getItem(TOKEN_STORAGE_KEYS[key]);
    } catch {
      return null;
    }
  },

  /** Write a slot. Silently no-ops if storage is unavailable / quota-blocked. */
  write(key: TokenStorageKey, value: string): void {
    const storage = safeStorage();
    if (!storage) return;
    try {
      storage.setItem(TOKEN_STORAGE_KEYS[key], value);
    } catch {
      // Quota exceeded / private mode — degrade silently. Worst case
      // the user gets logged out on next reload.
    }
  },

  /** Remove a single slot. */
  clear(key: TokenStorageKey): void {
    const storage = safeStorage();
    if (!storage) return;
    try {
      storage.removeItem(TOKEN_STORAGE_KEYS[key]);
    } catch {
      // Ignore.
    }
  },

  /**
   * Wipe every auth-owned slot. Used on explicit sign-out and on
   * refresh-token failure (token chain invalidated server-side, so
   * the stale tokens are worthless and must be evicted).
   *
   * Iterates each key independently so a single throwing `removeItem`
   * doesn't strand later keys in place.
   */
  clearAll(): void {
    const storage = safeStorage();
    if (!storage) return;
    for (const key of Object.values(TOKEN_STORAGE_KEYS)) {
      try {
        storage.removeItem(key);
      } catch {
        // Continue clearing the rest.
      }
    }
  },
};
